import { defineStore } from "pinia"
import { Coordinates, LocationData } from "@/modules/care-nav/types/geolocation-types"
import { BenefitPlanNameEnum } from "@vitable/asclepius-api-client-ts"

// Define interface for care filters
export interface CareFilters {
  searchQuery: string
  specialtyId: string | null
  insuranceNetworkId: string | null
  tagId: string | null
  distance: string
}

export interface CareNavState {
  selectedPlan: BenefitPlanNameEnum
  selectedAdditionalPlan: BenefitPlanNameEnum | undefined | null
  location: LocationData
  careFilters: CareFilters
}

export const useCareNavStore = defineStore("careNavStore", {
  persist: true,
  state: (): CareNavState => ({
    selectedPlan: BenefitPlanNameEnum.VitablePrimaryCareEnhanced,
    selectedAdditionalPlan: undefined,
    location: {
      zipcode: "",
      city: "",
      coordinates: {
        latitude: null,
        longitude: null
      }
    },
    careFilters: {
      searchQuery: "",
      specialtyId: null,
      insuranceNetworkId: null,
      tagId: null,
      distance: "50" // Default distance
    }
  }),
  getters: {
    getZipcode(): string {
      return this.location.zipcode || ""
    },
    getCoordinates(): Coordinates {
      return this.location.coordinates
    },
    getLocation(): LocationData {
      return this.location
    },
    getSelectedPlan(): BenefitPlanNameEnum {
      return this.selectedPlan
    },
    getSelectedAdditionalPlan(): BenefitPlanNameEnum | undefined | null {
      return this.selectedAdditionalPlan
    },
    getCity(): string {
      return this.location.city || ""
    },
    // New getters for care filters
    getCareFilters(): CareFilters {
      return this.careFilters
    },
    getCareSearchQuery(): string {
      return this.careFilters.searchQuery
    },
    getCareSpecialtyId(): string | null {
      return this.careFilters.specialtyId
    },
    getCareInsuranceNetworkId(): string | null {
      return this.careFilters.insuranceNetworkId
    },
    getCareTagId(): string | null {
      return this.careFilters.tagId
    },
    getCareDistance(): string {
      return this.careFilters.distance
    }
  },
  actions: {
    setZipcode(newZipcode: string) {
      if (!newZipcode) return
      this.location.zipcode = newZipcode
    },

    setCoordinates(coordinates: Coordinates) {
      this.location.coordinates = coordinates
    },

    setLocation(locationData: LocationData) {
      this.location = locationData
    },

    setSelectedPlan(plan: BenefitPlanNameEnum) {
      this.selectedPlan = plan
    },

    setSelectedAdditionalPlan(plan: BenefitPlanNameEnum | undefined | null) {
      this.selectedAdditionalPlan = plan
    },

    setCity(newCity: string) {
      this.location.city = newCity
    },

    resetLocation() {
      this.location.zipcode = ""
      this.location.city = ""
      this.location.coordinates = {
        latitude: null,
        longitude: null
      }
    },

    // New actions for care filters
    setCareSearchQuery(searchQuery: string) {
      this.careFilters.searchQuery = searchQuery
    },

    setCareSpecialtyId(specialtyId: string | null) {
      this.careFilters.specialtyId = specialtyId
    },

    setCareInsuranceNetworkId(insuranceNetworkId: string | null) {
      this.careFilters.insuranceNetworkId = insuranceNetworkId
    },

    setCareTagId(tagId: string | null) {
      this.careFilters.tagId = tagId
    },

    setCareDistance(distance: string) {
      this.careFilters.distance = distance
    },

    setCareFilters(filters: Partial<CareFilters>) {
      this.careFilters = { ...this.careFilters, ...filters }
    },

    resetCareFilters() {
      this.careFilters = {
        searchQuery: "",
        specialtyId: null,
        insuranceNetworkId: null,
        tagId: null,
        distance: "50"
      }
    }
  }
})
